import _map from 'lodash/map'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _isNil from 'lodash/isNil'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _compact from 'lodash/compact'
import _filter from 'lodash/filter'
import _orderBy from 'lodash/orderBy'
import _reduce from 'lodash/reduce'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _toString from 'lodash/toString'
import {
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  PLAN_SUBSCRIPTION_LIVE,
  PLAN_SUBSCRIPTION_UNKNOWN,
} from '.'

export const BILLING_PLAN_GRID_LOCATION_CHECKOUT = 'checkout'
export const BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN = 'planChange'
export const BILLING_PLAN_GRID_LOCATION_GIFT = 'gift'

export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_RECURRING = 'Recurring'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_GIFT = 'Gift'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_COURSE = 'Course'

export const BILLING_PLAN_TYPE_MONTHLY = 'Monthly'
export const BILLING_PLAN_TYPE_ANNUAL = 'Annual'
export const BILLING_PLAN_TYPE_GAIA_PLUS = 'Gaia+'

export const BILLING_PLAN_PERIOD_WEEK = 'week'
export const BILLING_PLAN_PERIOD_MONTH = 'month'
export const BILLING_PLAN_PERIOD_YEAR = 'year'

export const PRICE_REPLACEMENT_TYPE_NONE = 'none'
export const PRICE_REPLACEMENT_TYPE_PLAN = 'plan'
export const PRICE_REPLACEMENT_TYPE_NEXT_PLAN = 'nextPlan'
export const PRICE_REPLACEMENT_TYPE_FUTURE_PLAN = 'futurePlan'

export const PLAN_SCHEMA_LEGACY = {
  segments: [],
  heading: '',
  entitlementDays: null,
  shortDetails: '',
  costs: [],
  currencySymbol: '',
  billingPeriodAbbreviation: '',
  name: '',
  billingSchedule: '',
  duration: '',
  currencyIso: '',
  firstPayment: null,
  title: '',
  id: '',
  checkoutEnabled: true,
  billingPeriod: [],
  sku: '',
  valueProps: [],
  legacyType: '',
  pricePerWeek: null,
}

/**
 * Normalize GET_USER_PLAN_GRID_LIST (or similar) payload for {@link formatBillingPlans}.
 *
 * GraphQL nests the grid under `userBillingPlanGridList.userBillingPlanGrids[]`; we read the first
 * desktop match from the query `where` (same ordering the API returns for a single device).
 *
 * Products may appear as `billingPlanGrid.billingProviderProducts` (user grid) or under
 * `billingPlanGrid.billingProviderProductList.billingProviderProducts` (same shape as the root
 * `GET_PLANS` query). {@link formatBillingPlans} only understands the latter, so we always emit
 * `plansData.billingProviderProductList.billingProviderProducts`.
 *
 * `bestValueBillingProviderProductId` is stringified (Lodash `toString`) so it lines up with
 * `billingProviderProduct.id` in {@link formatBillingPlans} when the API mixes Int and ID scalars.
 *
 * @param {Object} gqlPlanData - Apollo `data` for UserBillingPlanGridList
 * @returns {{ plansData: Object, bestValueBillingProviderProductId: string|null }}
 */
export function userPlanGridPayloadForFormat (gqlPlanData) {
  if (!gqlPlanData) {
    return { plansData: {}, bestValueBillingProviderProductId: null }
  }

  // First grid row: query filters by page/device/language so one row is typical for Desktop.
  const billingPlanGrid = _get(gqlPlanData, [
    'userBillingPlanGridList',
    'userBillingPlanGrids',
    0,
    'billingPlanGrid',
  ])

  if (!billingPlanGrid) {
    return { plansData: {}, bestValueBillingProviderProductId: null }
  }

  const nestedList = _get(billingPlanGrid, 'billingProviderProductList')
  const nestedProducts = _get(nestedList, 'billingProviderProducts')
  const directProducts = _get(billingPlanGrid, 'billingProviderProducts')
  let billingProviderProducts = []

  // Prefer GET_PLANS-shaped nesting when present; otherwise use the list on BillingPlanGrid.
  // _compact removes nulls from GraphQL Maybe[] product lists (both branches).
  if (_isArray(nestedProducts) && !_isEmpty(nestedProducts)) {
    billingProviderProducts = _compact(nestedProducts)
  } else if (_isArray(directProducts)) {
    billingProviderProducts = _compact(directProducts)
  }

  const rawBestValueId = _get(billingPlanGrid, 'bestValueBillingProviderProductId')
  const bestValueBillingProviderProductId = _isNil(rawBestValueId) || rawBestValueId === ''
    ? null
    : _toString(rawBestValueId)

  return {
    plansData: {
      billingProviderProductList: { billingProviderProducts },
    },
    bestValueBillingProviderProductId,
  }
}

/**
 * Format/transform GraphQL plan data into the legacy plan data shape
 * @param {Object} plansData - GraphQL payload containing billingProviderProductList
 * @param {string|null} bestValueId - Billing provider product id for best value
 *   (must match GraphQL billingProviderProduct id; sets legacy bestValue)
 * @returns {Array} - Formatted array of plan data objects ordered
 * (monthly first, then annual, and finally gaia+)
 */
export function formatBillingPlans (plansData = {}, bestValueId = null) {
  if (!plansData) {
    return []
  }

  const plans = _get(plansData, ['billingProviderProductList', 'billingProviderProducts']) || []

  return _map(plans, (plan) => {
    const costs = getBillingPlanCosts(plan) || []

    return (_assign(_cloneDeep(PLAN_SCHEMA_LEGACY), {
      id: _get(plan, 'providerFirstSegmentId'),
      sku: _get(plan, 'sku', ''),
      type: _get(plan, ['billingProduct', 'name']),
      entitlementContentSet: _get(plan, ['billingProduct', 'contentSetId']),
      legacyType: getBillingPlanLegacyType(plan),
      subscriptionType: _get(plan, 'type'),
      compositeType: `${_get(plan, 'termLength')}${_get(plan, 'termType')}${_get(plan, ['billingProduct', 'name'])}${costs[0] || '0'}`,
      title: getBillingPlanTextItemValue(plan, 'title'),
      heading: getBillingPlanTextItemValue(plan, 'heading'),
      name: getBillingPlanTextItemValue(plan, 'name'),
      duration: getBillingPlanTextItemValue(plan, 'duration'),
      firstPayment: _get(costs, 0),
      billingPeriodAbbreviation: getBillingPlanTextItemValue(plan, 'billingPeriodAbbreviation'),
      billingSchedule: getBillingPlanTextItemValue(plan, 'billingSchedule'),
      billingPeriod: [
        getBillingPlanTextItemValue(plan, 'billingPeriod'),
      ],
      shortDetails: getBillingPlanTextItemValue(plan, 'shortDetails'),
      promoValue1: getBillingPlanTextItemValue(plan, 'promoValue1'),
      promoValue2: getBillingPlanTextItemValue(plan, 'promoValue2'),
      valueProps: getBillingPlanTextValueProps(plan),
      currencyIso: getBillingPlanPriceItemValue(plan, 'currency'),
      currencySymbol: getBillingPlanPriceItemValue(plan, 'currencySymbol'),
      checkoutEnabled: _get(plan, 'checkoutEnabled'),
      providerFirstSegmentId: _get(plan, 'providerFirstSegmentId'),
      providerSecondSegmentId: _get(plan, 'providerSecondSegmentId'),
      percentSavings: getBillingPlanPriceItemValue(plan, 'percentSavings'),
      trialPercentSavings: getBillingPlanPriceItemValue(plan, 'trialPercentSavings'),
      pricePerWeek: getBillingPlanPriceItemValue(plan, 'pricePerWeek'),
      costs,
      segments: getBillingPlanSegments(plan),
      changePlanAllowed: _get(plan, 'allowChangePlan'),
      bestValue: _get(plan, 'id') === bestValueId,
    }))
  })
}

/**
 * Get plan text item value
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired text item
 * @returns {String} - The plan text item value
 */
function getBillingPlanTextItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const textItem = _find(_get(plan, 'billingProviderProductText', {}), (i) => _get(i, 'key') === key && _get(i, 'enabled')) || {}
  return _get(textItem, 'value') || ''
}

/**
 * Get plan pricing item
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired pricing item
 * @returns {String} - The plan pricing item
 */
function getBillingPlanPriceItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const priceItem = _get(plan, ['billingProviderProductPrices', 0], {}) || {}
  return _get(priceItem, key)
}

/**
 * Get the plan enabled value props
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan value props that are enabled
 */
function getBillingPlanTextValueProps (plan) {
  const valueProps = _filter(_get(plan, 'billingProviderProductText'), (i) => _get(i, 'isValueProp') && _get(i, 'enabled')) || []
  const orderedValueProps = _orderBy(valueProps, ['valuePropOrder'], ['asc'])
  const valuePropArray = _reduce(orderedValueProps, (acc, valueProp) => {
    acc.push(_get(valueProp, 'value'))
    return acc
  }, [])
  return valuePropArray
}

/**
 * Get plan billing segments
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan billing segments
 */
function getBillingPlanSegments (plan) {
  const {
    termLength,
    termType,
    trialLength,
    trialTermType,
  } = plan
  const currency = getBillingPlanPriceItemValue(plan, 'currency')
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const segments = []

  if (trialLength) {
    segments.push({
      period: getFormattedBillingPlanPeriod(trialTermType, trialLength),
      price: _toString(trialPrice),
      currencyIso: currency,
    })
  }

  segments.push({
    period: getFormattedBillingPlanPeriod(termType, termLength),
    price: _toString(price),
    currencyIso: currency,
  })

  return segments
}

/**
 * Convert the plan type to the legacy plan type
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy type
 */
export function getBillingPlanLegacyType (plan) {
  const realType = _get(plan, ['billingProduct', 'name'])

  switch (realType) {
    case BILLING_PLAN_TYPE_MONTHLY:
      return PLAN_SUBSCRIPTION_MONTHLY
    case BILLING_PLAN_TYPE_ANNUAL:
      return PLAN_SUBSCRIPTION_ANNUAL
    case BILLING_PLAN_TYPE_GAIA_PLUS:
      return PLAN_SUBSCRIPTION_LIVE
    default:
      return PLAN_SUBSCRIPTION_UNKNOWN
  }
}

/**
 * Get the formatted billing period
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy billing period
 */
function getFormattedBillingPlanPeriod (termType, termLength) {
  const periodAbbr = 'P'
  let length = termLength
  let term = 'M'

  // transform "1 year" to "12 months"
  if (termType === BILLING_PLAN_PERIOD_YEAR) {
    length = 12
  }

  // transform "1 week" to "7 days"
  if (termType === BILLING_PLAN_PERIOD_WEEK) {
    term = 'D'
    length = 7 * termLength
  }

  return `${periodAbbr}${length}${term}`
}

/**
 * Get the plan costs
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan costs
 */
function getBillingPlanCosts (plan) {
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const costs = []

  // allow "zero" to be added as a cost
  // if trialPrice is null or undefined, then there is no trial
  if (!_isNil(trialPrice)) {
    costs.push(_toString(trialPrice))
  }

  costs.push(_toString(price))

  return costs
}
